/**
 * Assistance Request submit form (AR.5) — a modal the operator uses to lodge a
 * request for provincial assistance on a municipality / First Nation / agency's
 * behalf (Ontario IMS / PERP §5 — the PEOC is their single point of contact).
 *
 * Fields: requestor identity + contact, urgency, multi-select resource categories,
 * free-text narrative, and an optional link to the currently-selected incident
 * (the server snapshots that incident + derives a Response Level). Escape-to-close
 * mirrors CameraOverlay / SitrepDetailPanel. Submission is audited server-side
 * (`submit_assistance_request`).
 */
import { useEffect, useState } from 'react';
import type {
  AssistanceRequest,
  AssistanceUrgency,
  RequestorType,
  ResourceCategory,
} from '@tsr/shared-types';
import { CANADA_BBOX, createAssistanceRequest, fetchCommunities } from './api';
import type { SitrepIncidentSelection } from './SitrepControls';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

/** A community the operator can drop a request onto (auto-fills the coordinates).
 *  Sourced from the W.2 community registry — usually the requestor itself. */
interface LocationCommunity {
  id: string;
  name: string;
  jurisdiction: string | null;
  lat: number;
  lon: number;
}

const REQUESTOR_TYPES: { value: RequestorType; label: string }[] = [
  { value: 'municipality', label: 'Municipality' },
  { value: 'first_nation', label: 'First Nation' },
  { value: 'agency', label: 'Agency' },
  { value: 'other', label: 'Other' },
];

const URGENCIES: { value: AssistanceUrgency; label: string }[] = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'immediate', label: 'Immediate' },
];

const CATEGORIES: { value: ResourceCategory; label: string }[] = [
  { value: 'shelter', label: 'Shelter' },
  { value: 'water', label: 'Water' },
  { value: 'medical', label: 'Medical' },
  { value: 'transport', label: 'Transport' },
  { value: 'aircraft', label: 'Aircraft' },
  { value: 'crew', label: 'Crew' },
  { value: 'other', label: 'Other' },
];

/** Map the SITREP incident selection's type onto the assistance link type. */
function incidentLinkType(t: SitrepIncidentSelection['type']): 'active_fire' | 'high_risk_hotspot' {
  return t;
}

export function AssistanceRequestForm({
  selectedIncident,
  onClose,
  onSubmitted,
}: {
  selectedIncident: SitrepIncidentSelection | null;
  onClose: () => void;
  onSubmitted: (request: AssistanceRequest) => void;
}): JSX.Element {
  const [requestorType, setRequestorType] = useState<RequestorType>('municipality');
  const [requestorName, setRequestorName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [urgency, setUrgency] = useState<AssistanceUrgency>('urgent');
  const [categories, setCategories] = useState<Set<ResourceCategory>>(new Set());
  const [details, setDetails] = useState('');
  const [narrative, setNarrative] = useState('');
  const [linkIncident, setLinkIncident] = useState<boolean>(!!selectedIncident);
  // Optional location capture (#426): a community quick-pick that auto-fills the
  // coordinates, plus manual lat/lon — the lat/lon pair is the submitted source of
  // truth (persisted as geographic_scope). Both blank ⇒ omitted (backward-compatible).
  const [scopeLat, setScopeLat] = useState('');
  const [scopeLon, setScopeLon] = useState('');
  const [communities, setCommunities] = useState<LocationCommunity[]>([]);
  const [communityId, setCommunityId] = useState('');
  const [busy, setBusy] = useState(false);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Load the community registry once for the quick-pick (national bbox, best-effort).
  // An empty / failed fetch simply hides the picker — manual lat/lon still works, so
  // the location capture degrades gracefully and never blocks submitting a request.
  useEffect(() => {
    const ctl = new AbortController();
    void fetchCommunities(CANADA_BBOX, ctl.signal)
      .then((fc) => {
        const opts = fc.features
          .map((f) => ({
            id: f.id,
            name: f.properties.name,
            jurisdiction: f.properties.jurisdiction,
            lon: f.geometry.coordinates[0],
            lat: f.geometry.coordinates[1],
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCommunities(opts);
      })
      .catch(() => undefined);
    return () => ctl.abort();
  }, []);

  // Pick a community → drop its centroid into the coordinate fields.
  const pickCommunity = (id: string): void => {
    setCommunityId(id);
    const c = communities.find((x) => x.id === id);
    if (c) {
      setScopeLat(String(c.lat));
      setScopeLon(String(c.lon));
    }
  };

  const clearLocation = (): void => {
    setCommunityId('');
    setScopeLat('');
    setScopeLon('');
  };

  const toggleCategory = (c: ResourceCategory): void => {
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const submit = (): void => {
    if (busy) return;
    if (!requestorName.trim()) {
      showToast('Requestor name is required.', { variant: 'error' });
      return;
    }
    if (categories.size === 0) {
      showToast('Select at least one resource category.', { variant: 'error' });
      return;
    }
    if (!narrative.trim()) {
      showToast('A request narrative is required.', { variant: 'error' });
      return;
    }

    // Optional location: both blank → omit (backward-compatible). One blank, or out
    // of range → block. Both valid → persist as a map-locatable geographic_scope.
    const latStr = scopeLat.trim();
    const lonStr = scopeLon.trim();
    let scope: { lat: number; lon: number } | undefined;
    if (latStr || lonStr) {
      const lat = Number(latStr);
      const lon = Number(lonStr);
      if (!latStr || !lonStr || !Number.isFinite(lat) || !Number.isFinite(lon)) {
        showToast('Enter both latitude and longitude, or clear the location.', { variant: 'error' });
        return;
      }
      if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
        showToast('Location is out of range (lat −90…90, lon −180…180).', { variant: 'error' });
        return;
      }
      scope = { lat, lon };
    }

    const contact: Record<string, string> = {};
    if (contactName.trim()) contact.name = contactName.trim();
    if (contactEmail.trim()) contact.email = contactEmail.trim();
    if (contactPhone.trim()) contact.phone = contactPhone.trim();
    if (contactRole.trim()) contact.role = contactRole.trim();

    const link = linkIncident && selectedIncident;
    setBusy(true);
    void createAssistanceRequest({
      requestor_type: requestorType,
      requestor_name: requestorName.trim(),
      requestor_contact: Object.keys(contact).length > 0 ? contact : undefined,
      incident_link_type: link ? incidentLinkType(selectedIncident.type) : undefined,
      incident_link_id: link ? selectedIncident.id : undefined,
      urgency,
      geographic_scope: scope,
      requested_resources: {
        categories: [...categories],
        details: details.trim() || undefined,
      },
      request_narrative: narrative.trim(),
      session_id: getSessionId(),
    })
      .then((request) => {
        showToast('Assistance request submitted.', { variant: 'success' });
        onSubmitted(request);
      })
      .catch(() => showToast('Could not submit the request. Please retry.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';

  return (
    <div
      data-testid="assistance-form-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="assistance-form-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-lg max-h-[88vh] rounded-lg border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold tracking-tight">New Assistance Request</h2>
          <button
            type="button"
            data-testid="assistance-form-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-400 space-y-1">
              <span>Requestor type</span>
              <select
                data-testid="assistance-requestor-type"
                value={requestorType}
                onChange={(e) => setRequestorType(e.target.value as RequestorType)}
                className={inputCls}
              >
                {REQUESTOR_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[11px] text-slate-400 space-y-1">
              <span>Urgency</span>
              <select
                data-testid="assistance-urgency"
                value={urgency}
                onChange={(e) => setUrgency(e.target.value as AssistanceUrgency)}
                className={inputCls}
              >
                {URGENCIES.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Requestor name *</span>
            <input
              data-testid="assistance-requestor-name"
              value={requestorName}
              onChange={(e) => setRequestorName(e.target.value)}
              placeholder="e.g. Town of Bracebridge"
              className={inputCls}
            />
          </label>

          <fieldset className="border border-slate-700 rounded px-2.5 py-2">
            <legend className="text-[10px] uppercase tracking-wide text-slate-500 px-1">
              Contact (optional)
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Name"
                className={inputCls}
              />
              <input
                value={contactRole}
                onChange={(e) => setContactRole(e.target.value)}
                placeholder="Role (e.g. CEMC)"
                className={inputCls}
              />
              <input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Email"
                className={inputCls}
              />
              <input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Phone"
                className={inputCls}
              />
            </div>
          </fieldset>

          <fieldset className="border border-slate-700 rounded px-2.5 py-2" data-testid="assistance-location">
            <legend className="text-[10px] uppercase tracking-wide text-slate-500 px-1">
              Location (optional)
            </legend>
            {communities.length > 0 && (
              <select
                data-testid="assistance-location-community"
                aria-label="Community"
                value={communityId}
                onChange={(e) => pickCommunity(e.target.value)}
                className={`${inputCls} mb-2`}
              >
                <option value="">Pick a community — auto-fills coordinates…</option>
                {communities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.jurisdiction ? `${c.name} — ${c.jurisdiction}` : c.name}
                  </option>
                ))}
              </select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="assistance-location-lat"
                aria-label="Latitude"
                value={scopeLat}
                onChange={(e) => {
                  setScopeLat(e.target.value);
                  setCommunityId('');
                }}
                inputMode="decimal"
                placeholder="Latitude (e.g. 52.28)"
                className={inputCls}
              />
              <input
                data-testid="assistance-location-lon"
                aria-label="Longitude"
                value={scopeLon}
                onChange={(e) => {
                  setScopeLon(e.target.value);
                  setCommunityId('');
                }}
                inputMode="decimal"
                placeholder="Longitude (e.g. -81.86)"
                className={inputCls}
              />
            </div>
            {(scopeLat || scopeLon) && (
              <button
                type="button"
                data-testid="assistance-location-clear"
                onClick={clearLocation}
                className="mt-1 text-[10px] text-slate-400 hover:text-slate-200 underline"
              >
                Clear location
              </button>
            )}
            <p className="mt-1 text-[10px] text-slate-500">
              Sets a map-locatable point so the request can be shown on the map and correlated
              to hazards, cameras, and resources. The narrative still works without it.
            </p>
          </fieldset>

          <div className="text-[11px] text-slate-400 space-y-1">
            <span>Requested resources *</span>
            <div data-testid="assistance-categories" className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => {
                const on = categories.has(c.value);
                return (
                  <button
                    key={c.value}
                    type="button"
                    data-testid={`assistance-cat-${c.value}`}
                    onClick={() => toggleCategory(c.value)}
                    className={`text-xs px-2 py-1 rounded border ${
                      on
                        ? 'bg-emerald-600/80 border-emerald-500 text-white'
                        : 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <input
              data-testid="assistance-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Quantities / specifics (optional)"
              className={`${inputCls} mt-1`}
            />
          </div>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Request narrative *</span>
            <textarea
              data-testid="assistance-narrative"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              rows={3}
              placeholder="Describe the situation and what is needed."
              className={`${inputCls} resize-none`}
            />
          </label>

          {selectedIncident && (
            <label className="flex items-center gap-2 text-[11px] text-slate-300">
              <input
                type="checkbox"
                data-testid="assistance-link-incident"
                checked={linkIncident}
                onChange={(e) => setLinkIncident(e.target.checked)}
              />
              <span>Link to selected incident — {selectedIncident.label}</span>
            </label>
          )}
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700">
          <button
            type="button"
            data-testid="assistance-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs py-1.5 px-3 rounded bg-emerald-600/90 hover:bg-emerald-600 border border-emerald-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Submitting…' : 'Submit request'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
